import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, X } from 'lucide-react'

import { cn } from '@/utils/common'

import { Input } from '../ui/input'

export type ComboboxOption = {
  value: string
  label: string
  disabled?: boolean
}

export type ComboboxProps = {
  label?: string
  className?: string
  options: ComboboxOption[]
  placeholder?: string
  noMatchesText?: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  disabled?: boolean
  fullWidth?: boolean
  clearable?: boolean
  maxHeight?: number
  contentWidth?: number | string
}

export const Combobox: React.FC<ComboboxProps> = ({
  label,
  className,
  options,
  placeholder = 'Select an option',
  noMatchesText = 'No matches found.',
  value,
  defaultValue,
  onChange,
  disabled = false,
  fullWidth = false,
  clearable = false,
  maxHeight = 400,
  contentWidth,
}) => {
  const controlled = value !== undefined
  const [inner, setInner] = useState<string>(defaultValue || '')
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map())

  useEffect(() => {
    if (!controlled) setInner(defaultValue || '')
  }, [controlled, defaultValue])

  const selectedValue = controlled ? value : inner || ''

  const setValue = useCallback(
    (v: string) => {
      if (!controlled) setInner(v)
      onChange?.(v)
      setIsOpen(false)
      setTimeout(() => {
        setSearchQuery('')
      }, 0)
    },
    [controlled, onChange]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setValue('')
      setSearchQuery('')
    },
    [setValue]
  )

  const sortedOptions = useMemo(() => {
    return options
      .filter((opt) => opt.value !== '')
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [options])

  const filteredOptions = useMemo(
    () =>
      sortedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [sortedOptions, searchQuery]
  )

  const selectedOption = useMemo(
    () => sortedOptions.find((opt) => opt.value === selectedValue),
    [sortedOptions, selectedValue]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    if (!isOpen) setIsOpen(true)
    setHighlightedIndex(0)

    if (newValue === '' && selectedValue) {
      if (!controlled) setInner('')
      onChange?.('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        if (!isOpen) {
          setIsOpen(true)
        } else if (filteredOptions.length > 0) {
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          )
        }
        break
      case 'ArrowUp':
        if (isOpen && filteredOptions.length > 0) {
          e.preventDefault()
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        }
        break
      case 'Enter':
        e.preventDefault()
        if (isOpen && filteredOptions.length > 0) {
          setValue(filteredOptions[highlightedIndex].value)
        } else if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break
      case 'Tab':
        setIsOpen(false)
        break
    }
  }

  const handleOptionClick = (optionValue: string) => {
    setValue(optionValue)
  }

  useEffect(() => {
    if (
      isOpen &&
      highlightedIndex >= 0 &&
      highlightedIndex < filteredOptions.length
    ) {
      const highlightedElement = itemRefs.current.get(
        filteredOptions[highlightedIndex]?.value
      )
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
      }
    }
  }, [highlightedIndex, isOpen, filteredOptions])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  // Update search query to show selected value when not searching
  useEffect(() => {
    if (!isOpen && selectedValue && selectedOption) {
      setSearchQuery(selectedOption.label)
    } else if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen, selectedOption, selectedValue])

  const displayValue = searchQuery || selectedOption?.label || ''

  return (
    <div className={cn('relative', className)}>
      {label && (
        <label className="block mb-1 text-sm font-medium text-[--Colors-Gray-light-700]">
          {label}
        </label>
      )}

      <PopoverPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <PopoverPrimitive.Trigger asChild>
          <div
            className={cn(
              'relative flex items-center',
              fullWidth ? 'w-full' : 'max-w-[320px]'
            )}
          >
            <Input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls="combobox-listbox"
              aria-autocomplete="list"
              value={displayValue}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (!isOpen) {
                  setIsOpen(true)
                }
              }}
              onClick={(e) => {
                e.stopPropagation()
                if (!isOpen) {
                  setIsOpen(true)
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                'pr-7',
                !isOpen && selectedValue && 'truncate',
                disabled && 'cursor-not-allowed opacity-60'
              )}
            />
            <div
              className={cn(
                'absolute flex items-center right-2',
                disabled && 'pointer-events-none'
              )}
            >
              {clearable && selectedValue && !disabled ? (
                <X
                  className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleClear(e)
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  aria-label="Clear selection"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleClear(e as unknown as React.MouseEvent)
                    }
                  }}
                />
              ) : (
                <ChevronDown
                  className={cn(
                    'w-4 h-4 text-gray-500 transition-transform',
                    isOpen && 'transform rotate-180',
                    disabled && 'opacity-50'
                  )}
                />
              )}
            </div>
          </div>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            ref={listRef}
            align="start"
            sideOffset={4}
            style={(() => {
              if (contentWidth) {
                return {
                  width:
                    typeof contentWidth === 'number'
                      ? `${contentWidth}px`
                      : contentWidth,
                }
              }
              if (fullWidth) {
                return { width: 'var(--radix-popover-trigger-width)' }
              }
              return undefined
            })()}
            className={cn(
              'z-50 rounded-lg border border-solid border-[--Colors-Gray-light-300] bg-white shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            )}
            onOpenAutoFocus={(e) => {
              e.preventDefault()
              inputRef.current?.focus()
            }}
            onInteractOutside={(e) => {
              const target = e.target as HTMLElement
              if (
                target === inputRef.current ||
                inputRef.current?.contains(target)
              ) {
                e.preventDefault()
              }
            }}
            onPointerDownOutside={(e) => {
              const target = e.target as HTMLElement
              if (
                target === inputRef.current ||
                inputRef.current?.contains(target)
              ) {
                e.preventDefault()
              }
            }}
          >
            {/* eslint-disable-next-line -- Custom combobox requires ARIA listbox role - native <select> not applicable with Radix UI Popover pattern */}
            <div
              id="combobox-listbox"
              role="listbox"
              tabIndex={0}
              aria-label={label || 'Combobox options'}
              data-custom-combobox="true"
              className="overflow-y-auto overscroll-contain focus:outline-none"
              style={{ maxHeight: `${maxHeight}px` }}
              onWheel={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {filteredOptions.length === 0 ? (
                <div className="py-4 text-sm text-center text-gray-500">
                  {noMatchesText}
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === selectedValue
                  const isHighlighted = index === highlightedIndex
                  return (
                    // eslint-disable-next-line -- Custom combobox requires ARIA option role - native <option> not applicable with Radix UI Popover pattern
                    <div
                      key={option.value}
                      role="option"
                      data-custom-combobox-option="true"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                      tabIndex={-1}
                      ref={(el) => {
                        if (el) {
                          itemRefs.current.set(option.value, el)
                        } else {
                          itemRefs.current.delete(option.value)
                        }
                      }}
                      onClick={() =>
                        !option.disabled && handleOptionClick(option.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          !option.disabled && handleOptionClick(option.value)
                        }
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        'relative flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer select-none break-all',
                        isHighlighted && 'bg-accent text-accent-foreground',
                        isSelected && 'font-medium',
                        option.disabled &&
                          'opacity-50 cursor-not-allowed pointer-events-none'
                      )}
                    >
                      <span className="flex-1">{option.label}</span>
                      {isSelected && (
                        <Check className="flex-shrink-0 size-4 text-primary-600" />
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  )
}
